import Button from "@/Components/Button/Button";
import Error from "@/Components/Error";
import Modal, { ModalProps } from "@/Components/Modal/Modal";
import Spinner from "@/Components/Spinner/Spinner";
import { useModalMeta } from "@/shared/atoms/useModal";
import { useSetCart } from "@/shared/hooks/useCartHook";
import useMenuItemHook from "@/shared/hooks/useMenuItemHook";
import useUserOrdersInPosQuery from "@/shared/queries/useUserOrdersInPos/useUserOrdersInPosQuery";
import { MenuItems } from "@/shared/utils/enum";
import { asArray, formatDate, getValue } from "@/shared/utils/helpers";
import { Product, Topping } from "@/shared/utils/interfaces";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const CustomerOrderHistoryModal = ({ onExit }: ModalProps) => {
  const { t } = useTranslation();
  const [{ userId }] = useModalMeta();
  const { query, data } = useUserOrdersInPosQuery(userId);
  const { data: products } = useMenuItemHook<Product[]>(MenuItems.PRODUCT);
  const { data: toppings } = useMenuItemHook<Topping[]>(MenuItems.TOPPING);

  const [inValidOrders, setInValidOrders] = useState([]);
  const setCart = useSetCart();

  const handleAddCart = (order: OrderV2) => {
    const cart = order.cart_content.filter((i) => i.type === "1");

    const cartWithCurrentPrice = cart.map((cartItem) => {
      const currentCartItem = products?.find(
        (p) => p.item_id === +cartItem["menu_item_id"],
      );
      const currentToppings = asArray(cartItem.toppings)?.map((ct: any) => {
        const currentTopping = toppings?.find(
          (t) => t.item_id === +ct["menu_item_id"],
        );

        return currentTopping
          ? {
              item_id: currentTopping.item_id,
              price: currentTopping?.price,
              parent_id: +ct.parent_id,
              name: currentTopping.name,
              quantity: +cartItem.quantity,
            }
          : undefined;
      });

      return currentCartItem
        ? {
            price: currentCartItem?.price,
            item_id: currentCartItem.item_id,
            parent_id: currentCartItem.parent_id,
            name: currentCartItem.name,
            quantity: +cartItem.quantity,
            sub_content: currentToppings,
          }
        : undefined;
    });

    const isCartValid = cartWithCurrentPrice.every(
      (item) =>
        item !== undefined &&
        (item.sub_content
          ? item.sub_content.every((i) => i !== undefined)
          : true),
    );

    if (isCartValid) {
      setCart((cart) => [...cart, ...cartWithCurrentPrice]);
      onExit();
    } else {
      setInValidOrders((prev) => [
        ...prev,
        getValue(order?.order_information?.item_id),
      ]);
    }
  };

  return (
    <Modal width="120rem" maxHeight="80rem" onExit={onExit}>
      {query.isFetching && <Spinner />}
      {!query.isFetching && query.isError && <Error refetch={query.refetch} />}
      {!query.isFetching && query.isSuccess && (
        <>
          <h2>{t("customerOrderHistory.orderHistoryOfCustomer")}</h2>
          {data?.map((order) => (
            <div key={order?.order_information?.item_id} className="bg-[var(--table-blue)] rounded-[5px] mb-[1rem]">
              <div className="p-[1.5rem] pb-[0.5rem]">
                {order?.cart_content
                  ?.filter((i) => i.type === "1")
                  .map((cart) => (
                    <>
                      <div className="mb-[1rem]" style={{ fontWeight: "bold" }}>
                        {`${cart.quantity} x ${cart.name}`}
                      </div>
                      {cart?.toppings && (
                        <div className="text-[1.4rem] mt-[-1rem]">
                          {asArray(cart?.toppings)
                            ?.map((t) => t.name)
                            ?.join(",")}
                        </div>
                      )}
                    </>
                  ))}
              </div>
              <div className="flex p-6" style={{ borderTop: "2px solid var(--white)" }}>
                <div className="flex-[1_0_25%]">
                  <div style={{ fontWeight: "bold" }}>
                    {t("customerOrderHistory.orderDate")}
                  </div>
                  <div>
                    {formatDate(
                      order?.order_information?.placed_date_time,
                      "dateTime",
                    )}
                  </div>
                </div>

                <div className="flex gap-4 self-end">
                  {inValidOrders.includes(
                    order?.order_information?.item_id,
                  ) && (
                    <div className="self-[center]" style={{ fontWeight: "bold" }}>
                      {t("customerOrderHistory.containsRemovedProduct")}
                    </div>
                  )}
                  <Button
                    style={{ borderRadius: "0" }}
                    onClick={() => handleAddCart(order)}
                    isDisabled={inValidOrders.includes(
                      order?.order_information?.item_id,
                    )}
                  >
                    {t("customerOrderHistory.orderAgain").toUpperCase()}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
};

export default CustomerOrderHistoryModal;
